import { type ReactElement, useEffect, useState } from "react";
import {
  IconButton,
  type IconButtonProps,
  Tooltip,
  type TooltipProps,
} from "@mui/material";
import cx from "classnames";
import { ContentCopyIcon } from "@ui-icons";

const HIDE_TIMEOUT = 2000;

interface CopyTextProps {
  text: string | number;
  displayText?: string | number | ReactElement<any>;
  className?: string;
  buttonOnly?: boolean;
  hideText?: boolean;
  placeIconAtStart?: boolean;
  /** PostHog autocapture: what is being copied (e.g. `pipeline_id`, `error_message`). */
  phContext?: string;
  slotProps?: {
    iconButton?: Partial<IconButtonProps>;
    tooltip?: Partial<TooltipProps>;
  };
}

export const CopyText = ({
  text,
  displayText,
  className = "text-xs",
  buttonOnly,
  hideText,
  phContext,
  placeIconAtStart,
  slotProps,
}: CopyTextProps) => {
  const [showState, setShowState] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: OK
  useEffect(() => {
    const timout = setTimeout(() => {
      setShowState(false);
    }, HIDE_TIMEOUT);

    return () => {
      clearTimeout(timout);
    };
  }, [showState]);

  return (
    <div className={cx("inline-flex items-center", className)}>
      {text && !displayText && !buttonOnly && !hideText && (
        <span className={cx({ "order-2": placeIconAtStart })}>{text}</span>
      )}
      <div
        className="inline-flex cursor-pointer user-select-none"
        data-ph-capture-attribute-action="copy"
        {...(phContext !== undefined
          ? { "data-ph-capture-attribute-context": phContext }
          : {})}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          void navigator.clipboard.writeText(text + "");
          setShowState(true);
        }}
      >
        <Tooltip
          title="Copied to clipboard!"
          open={showState}
          arrow
          disableInteractive
          {...slotProps?.tooltip}
        >
          <div className="inline-flex items-center">
            <IconButton
              sx={{
                width: 22,
                height: 22,
                p: 0.5,
                mx: slotProps?.iconButton?.edge ? 0 : 0.5,
              }}
              {...slotProps?.iconButton}
            >
              <ContentCopyIcon
                className="box-content cursor-pointer"
                sx={{ width: "0.8rem" }}
              />
            </IconButton>
            {!buttonOnly && displayText && <span>{displayText}</span>}
          </div>
        </Tooltip>
      </div>
    </div>
  );
};
